import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, Loader2, Trash2, Video } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamsSourcePage() {
  const { personId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [person, setPerson] = useState(null);
  const [teamsSource, setTeamsSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isConnectOpen, setIsConnectOpen] = useState(false);

  const [connectForm, setConnectForm] = useState({
    teams_user_id: '',
    teams_user_email: '',
    phone_numbers: '',
    sync_calls: true,
    sync_meetings: true,
    sync_from_days: 30
  });
  const [teamsUsers, setTeamsUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isOwner = user?.role === 'owner';

  useEffect(() => {
    fetchPerson();
    fetchTeamsSource();
  }, [personId]);

  const fetchPerson = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerson(response.data);
    } catch (error) {
      toast.error('Clone not found');
      navigate('/clones');
    }
  };

  const fetchTeamsSource = async () => {
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/teams/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamsSource(response.data);
    } catch (error) {
      // 404 = not connected
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamsUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/persons/${personId}/sources/teams/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamsUsers(response.data.users || []);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Impossible de recuperer les utilisateurs Teams';
      toast.error(msg);
      if (msg.includes('credentials not configured')) {
        toast.info('Configurez vos identifiants Microsoft Teams dans Settings');
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleConnect = async () => {
    try {
      const phoneArr = connectForm.phone_numbers.split(',').map(p => p.trim()).filter(Boolean);
      await axios.post(`${API}/persons/${personId}/sources/teams`, {
        teams_user_id: connectForm.teams_user_id || null,
        teams_user_email: connectForm.teams_user_email || null,
        phone_numbers: phoneArr,
        sync_calls: connectForm.sync_calls,
        sync_meetings: connectForm.sync_meetings,
        sync_from_days: connectForm.sync_from_days
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Source Teams connectee');
      setIsConnectOpen(false);
      fetchTeamsSource();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/persons/${personId}/sources/teams/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Synchronisation Teams lancee');
      setTimeout(fetchTeamsSource, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer la source Teams ?')) return;
    try {
      await axios.delete(`${API}/persons/${personId}/sources/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Source Teams supprimee');
      setTeamsSource(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Microsoft Teams">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Microsoft Teams">
      <div className="max-w-3xl space-y-6" data-testid="teams-source-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/clones/${personId}/sources`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">
              <Video className="w-5 h-5 inline mr-2 text-indigo-500" />
              Microsoft Teams — {person?.name}
            </h2>
            <p className="text-sm text-muted-foreground">Appels PSTN + reunions avec transcription native</p>
          </div>
        </div>

        {teamsSource ? (
          <Card data-testid="teams-status-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading">Source Teams</CardTitle>
                <div className="flex items-center gap-2">
                  {teamsSource.last_sync_status === 'completed' && <Badge variant="outline" className="text-emerald-600 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Synchro OK</Badge>}
                  {teamsSource.last_sync_status === 'syncing' && <Badge variant="outline" className="text-amber-600 border-amber-200"><Clock className="w-3 h-3 mr-1" />Sync en cours</Badge>}
                  {teamsSource.last_sync_status === 'failed' && <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Erreur</Badge>}
                  {teamsSource.last_sync_status === 'pending' && <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />En attente</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Utilisateur Teams</p>
                  <p className="text-sm">{teamsSource.teams_user_email || teamsSource.teams_user_id || 'Tous'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Appels synchronises</p>
                  <p className="text-sm font-medium">{teamsSource.calls_synced_count || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Derniere sync</p>
                  <p className="text-sm">{teamsSource.last_sync_at ? new Date(teamsSource.last_sync_at).toLocaleString('fr-FR') : 'Jamais'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sync</p>
                  <div className="flex gap-2">
                    {teamsSource.sync_calls && <Badge variant="outline" className="text-xs">Appels</Badge>}
                    {teamsSource.sync_meetings && <Badge variant="outline" className="text-xs">Reunions</Badge>}
                  </div>
                </div>
              </div>

              {teamsSource.last_sync_error && (
                <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  {teamsSource.last_sync_error}
                </div>
              )}

              <Separator />

              <div className="flex items-center justify-between">
                <Button onClick={handleSync} disabled={syncing} size="sm" data-testid="sync-teams-btn">
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Synchroniser
                </Button>
                {isOwner && (
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={handleDelete} data-testid="delete-teams-btn">
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="teams-connect-card">
            <CardContent className="py-12 text-center space-y-4">
              <Video className="w-12 h-12 mx-auto text-indigo-500/40" />
              <div>
                <h3 className="font-heading font-semibold">Connecter Microsoft Teams</h3>
                <p className="text-sm text-muted-foreground mt-1">Appels PSTN et reunions avec transcription native</p>
              </div>
              <Button onClick={() => { setIsConnectOpen(true); fetchTeamsUsers(); }} data-testid="connect-teams-btn">
                Connecter Teams
              </Button>
            </CardContent>
          </Card>
        )}

        <Dialog open={isConnectOpen} onOpenChange={setIsConnectOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Connecter Microsoft Teams</DialogTitle>
              <DialogDescription>Selectionnez l'utilisateur Teams a associer a ce clone</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Utilisateur Teams</Label>
                {loadingUsers ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
                  </div>
                ) : teamsUsers.length > 0 ? (
                  <div className="grid gap-2 max-h-48 overflow-y-auto">
                    {teamsUsers.map(u => (
                      <button
                        key={u.id}
                        onClick={() => setConnectForm({
                          ...connectForm,
                          teams_user_id: u.id,
                          teams_user_email: u.email,
                          phone_numbers: u.phones?.join(', ') || ''
                        })}
                        className={`flex items-center justify-between p-3 rounded-md border text-left transition-colors ${
                          connectForm.teams_user_id === u.id ? 'border-indigo-500 bg-indigo-500/5' : 'border-border hover:border-indigo-300'
                        }`}
                        data-testid={`teams-user-${u.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{u.name}</p>
                          <p className="text-xs text-muted-foreground">{u.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">Aucun utilisateur. Verifiez les identifiants Azure AD dans Settings.</p>
                    <Input
                      placeholder="ID utilisateur Teams (GUID)"
                      value={connectForm.teams_user_id}
                      onChange={e => setConnectForm({ ...connectForm, teams_user_id: e.target.value })}
                      data-testid="teams-user-id-input"
                    />
                    <Input
                      placeholder="Email (user@company.com)"
                      value={connectForm.teams_user_email}
                      onChange={e => setConnectForm({ ...connectForm, teams_user_email: e.target.value })}
                      data-testid="teams-user-email-input"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Numeros de telephone</Label>
                <Input
                  placeholder="+33612345678"
                  value={connectForm.phone_numbers}
                  onChange={e => setConnectForm({ ...connectForm, phone_numbers: e.target.value })}
                  data-testid="teams-phones-input"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Appels PSTN</Label>
                <Switch checked={connectForm.sync_calls} onCheckedChange={v => setConnectForm({ ...connectForm, sync_calls: v })} data-testid="teams-sync-calls-switch" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Reunions Teams</Label>
                <Switch checked={connectForm.sync_meetings} onCheckedChange={v => setConnectForm({ ...connectForm, sync_meetings: v })} data-testid="teams-sync-meetings-switch" />
              </div>

              <div className="space-y-2">
                <Label>Historique (jours)</Label>
                <Input
                  type="number" min={1} max={365}
                  value={connectForm.sync_from_days}
                  onChange={e => setConnectForm({ ...connectForm, sync_from_days: parseInt(e.target.value) || 30 })}
                  data-testid="teams-sync-days-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsConnectOpen(false)}>Annuler</Button>
              <Button onClick={handleConnect} disabled={!connectForm.teams_user_id} data-testid="confirm-connect-teams-btn">Connecter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
